import { CitationCard } from './KnowledgeAnswer';
import { useState } from 'react';
import type { Attachment, Note } from '@shared/types';
import { relativeTime } from '../lib/format';
import { useToast } from '../lib/toast';
import { AttachmentGrid, AttachmentUploader } from './Attachments';
import { Tag } from './ui';

interface Props {
  notes: Note[];
  placeholder: string;
  /** Reviewers and board members can post notes the requester cannot see. */
  allowInternal?: boolean;
  onSubmit: (body: string, internal: boolean, attachmentIds: string[]) => Promise<void>;
  disabled?: boolean;
}

export function NoteThread({ notes, placeholder, allowInternal = false, onSubmit, disabled }: Props) {
  const { fail } = useToast();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit(body.trim(), internal, files.map((f) => f.id));
      setBody('');
      setFiles([]);
      setInternal(false);
    } catch (error) {
      fail(error, 'Your note was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="thread">
      {notes.map((note) => (
        <div key={note.id} className="note">
          <div className="note-head">
            <span className="who">{note.authorName}</span>
            <span className="mut">{relativeTime(note.createdAt)}</span>
            {note.internal && <Tag tone="warn">Board only</Tag>}
          </div>
          <p className="note-body">{note.body}</p>
          <AttachmentGrid items={note.attachments} />
          {note.citations && note.citations.length > 0 && (
            <details className="kc-drawer">
              <summary>
                {note.citations.length} cited source{note.citations.length === 1 ? '' : 's'}
              </summary>
              <ul className="kc-cites">
                {note.citations.map((citation, index) => (
                  <CitationCard key={`${citation.sourceId}-${index}`} citation={citation} />
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}

      {!disabled && (
        <div style={{ marginTop: 12 }}>
          <textarea
            rows={2}
            placeholder={placeholder}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          <AttachmentUploader value={files} onChange={setFiles} max={5} label="Attach" />
          {allowInternal && (
            <label className="check">
              <input
                type="checkbox"
                checked={internal}
                onChange={(event) => setInternal(event.target.checked)}
              />
              <span>Keep this note internal — the resident will not see it</span>
            </label>
          )}
          <div className="btn-row">
            <button type="button" className="btn s" disabled={!body.trim() || busy} onClick={() => void send()}>
              {busy ? 'Posting…' : 'Post note'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
